import React, { useEffect, useMemo, useRef, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../lib/firebase";

import { uploadFile, deleteFileByUrl } from "../lib/storageApi";
import {
  upsertEntry,
  listEntriesBySection,
  getEntryBySectionSlug,
  patchEntry,
  deleteEntry,
} from "../lib/entriesApi";
import { listSections, createSection } from "../lib/sectionsApi";
import {
  LayoutDashboard,
  LogOut,
  PlusCircle,
  Pencil,
  Images,
  Upload,
  Shield,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Image as ImageIcon,
  Trash2,
  Folder,
  FileText,
  Hash,
  Sparkles,
  X,
  PartyPopper,
  Camera,
} from "lucide-react";
import { createPortal } from "react-dom";

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .trim()
    .replace(/[’']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function fmtEta(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return "—";
  const s = Math.ceil(ms / 1000);
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

function fmtBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  const dec = i === 0 ? 0 : i === 1 ? 0 : 1;
  return `${v.toFixed(dec)} ${units[i]}`;
}

function Badge({ children, tone = "default" }) {
  const cls =
    tone === "dark"
      ? "bg-slate-900 text-white"
      : tone === "ok"
      ? "bg-emerald-50 text-emerald-800 ring-emerald-200"
      : tone === "warn"
      ? "bg-amber-50 text-amber-900 ring-amber-200"
      : tone === "err"
      ? "bg-rose-50 text-rose-900 ring-rose-200"
      : "bg-slate-100 text-slate-700 ring-slate-200";
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-black ring-1 ${cls}`}
    >
      {children}
    </span>
  );
}

function Card({ title, icon, right, children }) {
  return (
    <section className="relative overflow-hidden rounded-3xl border border-slate-200/80 bg-white/80 backdrop-blur-2xl shadow-[0_20px_80px_rgba(15,23,42,.10)]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(900px_340px_at_0%_0%,rgba(204,58,33,.10),transparent_60%),radial-gradient(700px_280px_at_100%_100%,rgba(251,191,36,.07),transparent_58%),linear-gradient(180deg,rgba(255,255,255,.65),rgba(248,250,252,.92))]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#CC3A21,#E58E7C,#F2C9A5)]" />

      <div className="relative flex items-center justify-between gap-4 border-b border-slate-900/10 bg-white/50 px-6 py-5">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-2xl bg-slate-900 text-white shadow-lg">
            {icon}
          </div>
          <div>
            <div className="text-sm font-black text-slate-900">{title}</div>
            <div className="text-[12px] font-semibold text-slate-500">
              Gestione contenuti + media
            </div>
          </div>
        </div>
        {right}
      </div>

      <div className="relative p-6">{children}</div>
    </section>
  );
}

function ProgressModal({ open, phase, progress, label, etaMs }) {
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape") e.preventDefault();
    };
    window.addEventListener("keydown", onKey, { capture: true });

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey, { capture: true });
    };
  }, [open]);

  if (!open) return null;

  const pill =
    phase === "error"
      ? { tone: "err", label: "errore" }
      : phase === "cover"
      ? { tone: "default", label: "cover" }
      : phase === "gallery"
      ? { tone: "default", label: "gallery" }
      : phase === "saving"
      ? { tone: "default", label: "salvataggio" }
      : phase === "delete"
      ? { tone: "warn", label: "delete" }
      : phase === "load"
      ? { tone: "default", label: "load" }
      : { tone: "default", label: "..." };

  return createPortal(
    <div className="fixed inset-0 z-[11000]">
      <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-md" />
      <div className="absolute inset-0 grid place-items-center p-4">
        <div className="relative w-full max-w-xl overflow-hidden rounded-[28px] border border-slate-200/80 bg-white/92 backdrop-blur-2xl shadow-2xl">
          <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#CC3A21]/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-amber-200/20 blur-3xl" />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#CC3A21,#E58E7C,#F2C9A5)]" />

          <div className="relative p-6">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="grid h-12 w-12 place-items-center rounded-3xl bg-slate-900 text-white shadow-xl">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
                <div className="min-w-0">
                  <div className="text-xl font-black text-slate-900">
                    Operazione in corso…
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-700">
                    Non chiudere: l’admin sta cucinando magia 🪄
                  </div>
                </div>
              </div>

              <div className="shrink-0 text-right">
                <Badge tone={pill.tone}>
                  <Sparkles className="h-3.5 w-3.5" />
                  {pill.label}
                </Badge>
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-slate-900/10 bg-white/80 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs font-black text-slate-700">Stato</div>
                  <div className="mt-1 truncate text-sm font-semibold text-slate-800">
                    {label || "Preparazione…"}
                  </div>
                </div>

                <div className="shrink-0 text-right">
                  <div className="rounded-2xl bg-slate-900 px-3 py-2 text-sm font-black tabular-nums text-white">
                    {clamp(progress, 0, 100)}%
                  </div>
                  <div className="mt-1 text-[11px] font-semibold text-slate-500">
                    ETA{" "}
                    <span className="font-black tabular-nums text-slate-700">
                      {fmtEta(etaMs)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-3 h-3 w-full overflow-hidden rounded-full bg-slate-900/10 ring-1 ring-slate-900/10">
                <div
                  className="h-full rounded-full bg-[linear-gradient(90deg,#CC3A21,#E58E7C,#F2C9A5)] shadow-[0_10px_25px_rgba(204,58,33,.22)] transition-all duration-300 ease-out"
                  style={{ width: `${clamp(progress, 0, 100)}%` }}
                />
              </div>

              <div className="mt-3 text-[12px] font-semibold text-slate-600">
                (Tip: se senti un rumore… è solo la GPU che applaude 👏)
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ResultModal({ open, tone = "ok", title, subtitle, lines = [], okText = "OK", onOk }) {
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onOk?.();
      }
    };
    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onOk]);

  if (!open) return null;

  const cfg =
    tone === "err"
      ? {
          ring: "border-rose-200/60",
          bg: "bg-[radial-gradient(ellipse_at_top,rgba(244,63,94,.20),transparent_55%),linear-gradient(135deg,rgba(255,255,255,.92),rgba(255,255,255,.78))]",
          iconWrap: "bg-rose-600",
          icon: <AlertTriangle className="h-6 w-6" />,
          btn: "bg-rose-600 hover:bg-rose-700",
          glow: "from-rose-300/40 via-white/0 to-transparent",
        }
      : tone === "warn"
      ? {
          ring: "border-amber-200/60",
          bg: "bg-[radial-gradient(ellipse_at_top,rgba(245,158,11,.18),transparent_55%),linear-gradient(135deg,rgba(255,255,255,.92),rgba(255,255,255,.78))]",
          iconWrap: "bg-amber-500",
          icon: <Sparkles className="h-6 w-6" />,
          btn: "bg-amber-500 hover:bg-amber-600",
          glow: "from-amber-300/35 via-white/0 to-transparent",
        }
      : {
          ring: "border-emerald-200/60",
          bg: "bg-[radial-gradient(ellipse_at_top,rgba(16,185,129,.18),transparent_55%),linear-gradient(135deg,rgba(255,255,255,.92),rgba(255,255,255,.78))]",
          iconWrap: "bg-emerald-600",
          icon: <PartyPopper className="h-6 w-6" />,
          btn: "bg-emerald-600 hover:bg-emerald-700",
          glow: "from-emerald-300/30 via-white/0 to-transparent",
        };

  return createPortal(
    <div className="fixed inset-0 z-[12000]">
      <div className="absolute inset-0 bg-slate-950/55 backdrop-blur-md" />
      <div className="absolute inset-0 grid place-items-center p-4">
        <div
          className={`relative w-full max-w-xl overflow-hidden rounded-[28px] border ${cfg.ring} ${cfg.bg} shadow-2xl`}
        >
          <div className={`pointer-events-none absolute inset-0 bg-gradient-to-b ${cfg.glow}`} />
          <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-white/25 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-amber-100/20 blur-3xl" />

          <div className="relative p-6">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className={`grid h-12 w-12 place-items-center rounded-3xl text-white shadow-xl ${cfg.iconWrap}`}>
                  {cfg.icon}
                </div>
                <div className="min-w-0">
                  <div className="text-xl font-black text-slate-900">{title}</div>
                  {subtitle ? (
                    <div className="mt-1 text-sm font-semibold text-slate-700">
                      {subtitle}
                    </div>
                  ) : null}
                </div>
              </div>

              <button
                onClick={onOk}
                className="rounded-2xl p-2 hover:bg-slate-900/5"
                aria-label="Chiudi"
                title="Chiudi"
              >
                <X className="h-5 w-5 text-slate-700" />
              </button>
            </div>

            {lines?.length ? (
              <div className="mt-4 grid gap-2">
                {lines.map((t, i) => (
                  <div
                    key={i}
                    className="rounded-2xl border border-slate-900/10 bg-white/75 px-4 py-3 text-sm font-semibold text-slate-800"
                  >
                    {t}
                  </div>
                ))}
              </div>
            ) : null}

            <div className="mt-5 flex justify-end">
              <button
                onClick={onOk}
                className={`inline-flex items-center gap-2 rounded-2xl px-5 py-3 text-sm font-black text-white shadow-xl transition ${cfg.btn}`}
              >
                <CheckCircle2 className="h-5 w-5" />
                {okText}
              </button>
            </div>

            <div className="mt-3 text-[12px] font-semibold text-slate-600">
              Premi OK per tornare al pannello admin.
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ConfirmDeleteModal({
  open,
  busy,
  section,
  selectedSlug,
  deleteAlsoFiles,
  setDeleteAlsoFiles,
  onClose,
  onConfirm,
}) {
  useEffect(() => {
    if (!open) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, busy, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={() => !busy && onClose()}
      />
      <div className="absolute inset-0 grid place-items-center p-4">
        <div className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-slate-200/80 bg-white/92 backdrop-blur-2xl shadow-2xl">
          <div className="pointer-events-none absolute -top-20 -right-20 h-56 w-56 rounded-full bg-rose-100/35 blur-3xl" />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#e11d48,#fb7185,#fecdd3)]" />

          <div className="flex items-center justify-between border-b border-slate-900/10 bg-white/70 p-5">
            <div className="flex items-center gap-2">
              <div className="grid h-10 w-10 place-items-center rounded-2xl bg-rose-600 text-white">
                <Trash2 className="h-5 w-5" />
              </div>
              <div>
                <div className="text-sm font-black text-slate-900">
                  Eliminare la card?
                </div>
                <div className="text-xs font-semibold text-slate-600">
                  {section} / <span className="font-black">{selectedSlug}</span>
                </div>
              </div>
            </div>
            <button
              disabled={busy}
              onClick={onClose}
              className="rounded-2xl p-2 hover:bg-slate-900/5 disabled:opacity-60"
              aria-label="Chiudi"
            >
              <X className="h-5 w-5 text-slate-700" />
            </button>
          </div>

          <div className="relative p-5">
            <div className="rounded-2xl border border-slate-900/10 bg-white p-4">
              <label className="flex cursor-pointer select-none items-start gap-3">
                <input
                  type="checkbox"
                  checked={deleteAlsoFiles}
                  disabled={busy}
                  onChange={(e) => setDeleteAlsoFiles(e.target.checked)}
                  className="mt-1"
                />
                <div className="min-w-0">
                  <div className="text-sm font-black text-slate-900">
                    Elimina anche i file su Storage
                  </div>
                  <div className="mt-1 text-xs font-semibold text-slate-600">
                    Cover + tutte le immagini in galleria verranno cancellate.
                  </div>
                </div>
              </label>
            </div>

            <div className="mt-4 flex gap-2">
              <button
                disabled={busy}
                onClick={onClose}
                className="w-1/2 rounded-2xl border border-slate-900/10 bg-white px-4 py-3 text-sm font-black text-slate-900 transition hover:bg-slate-50 disabled:opacity-60"
              >
                Annulla
              </button>
              <button
                disabled={busy}
                onClick={onConfirm}
                className="w-1/2 rounded-2xl bg-rose-600 px-4 py-3 text-sm font-black text-white shadow-lg transition hover:bg-rose-700 disabled:opacity-60"
              >
                {busy ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Eliminazione…
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    <Trash2 className="h-5 w-5" />
                    Elimina ora
                  </span>
                )}
              </button>
            </div>

            <div className="mt-4 text-[12px] font-semibold text-slate-600">
              Nota: azione irreversibile (come inviare “ok” senza emoji).
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function CreateSectionModal({
  open,
  busy,
  label,
  setLabel,
  order,
  setOrder,
  onClose,
  onConfirm,
}) {
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
  <div className="fixed inset-0 z-[10050]">
    <div
      className="absolute inset-0 bg-slate-950/60 backdrop-blur-[6px]"
      onClick={() => !busy && onClose()}
    />
    <div className="absolute inset-0 grid place-items-center p-4">
      <div className="w-full max-w-lg overflow-hidden rounded-3xl border border-slate-200 bg-[#fcfbfa] shadow-[0_30px_120px_rgba(2,6,23,.22)]">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#CC3A21,#E58E7C,#F2C9A5)]" />

        <div className="border-b border-slate-200 bg-white px-5 py-5">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-2xl bg-slate-900 text-white shadow-lg">
              <Folder className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-black text-slate-900">
                Crea nuova sezione
              </div>
              <div className="text-xs font-semibold text-slate-600">
                Verrà aggiunta automaticamente alla navigazione
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4 bg-[#fcfbfa] p-5">
          <div>
            <label className="text-xs font-black text-slate-700">
              Nome sezione
            </label>
            <input
              value={label}
              disabled={busy}
              onChange={(e) => setLabel(e.target.value)}
              className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900 shadow-[inset_0_1px_2px_rgba(15,23,42,.04)] outline-none placeholder:text-slate-400 focus:border-[#CC3A21]/40 focus:ring-4 focus:ring-[#CC3A21]/10"
              placeholder="Es. Collezioni rare"
            />
            <div className="mt-2 text-xs font-semibold text-slate-600">
              Slug auto: <span className="font-black">{slugify(label) || "—"}</span>
            </div>
          </div>

          <div>
            <label className="text-xs font-black text-slate-700">Ordine</label>
            <input
              type="number"
              value={order}
              disabled={busy}
              onChange={(e) => setOrder(e.target.value)}
              className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900 shadow-[inset_0_1px_2px_rgba(15,23,42,.04)] outline-none focus:border-[#CC3A21]/40 focus:ring-4 focus:ring-[#CC3A21]/10"
            />
          </div>

          <div className="flex gap-3 pt-1">
            <button
              disabled={busy}
              onClick={onClose}
              className="w-1/2 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-black text-slate-900 shadow-sm transition hover:bg-slate-50"
            >
              Annulla
            </button>
            <button
              disabled={busy}
              onClick={onConfirm}
              className="w-1/2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-black text-white shadow-[0_18px_40px_rgba(15,23,42,.18)] transition hover:bg-black disabled:opacity-60"
            >
              {busy ? "Creazione..." : "Crea sezione"}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>,
  document.body
)
};

export default function Admin() {
  const [sections, setSections] = useState([]);
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const [newSectionLabel, setNewSectionLabel] = useState("");
  const [newSectionOrder, setNewSectionOrder] = useState(999);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const [mode, setMode] = useState("create");
  const [section, setSection] = useState("fondi");

  const [titolo, setTitolo] = useState("");
  const slugAuto = useMemo(() => slugify(titolo), [titolo]);
  const [descrizione, setDescrizione] = useState("");

  const [coverFile, setCoverFile] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);

  const [entriesList, setEntriesList] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("");

  const [existingCoverUrl, setExistingCoverUrl] = useState("");
  const [existingImages, setExistingImages] = useState([]);

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const [phase, setPhase] = useState("");
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState("");

  const [etaMs, setEtaMs] = useState(null);
  const startMsRef = useRef(0);
  const durationsRef = useRef([]);
  const totalItemsRef = useRef(0);

  const PAGE_SIZE = 10;
  const [shownCount, setShownCount] = useState(PAGE_SIZE);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteAlsoFiles, setDeleteAlsoFiles] = useState(true);

  const [result, setResult] = useState({
    open: false,
    tone: "ok",
    title: "",
    subtitle: "",
    lines: [],
    okText: "OK",
  });

  useEffect(() => {
    if (mode !== "create") return;

    setTitolo("");
    setDescrizione("");

    setCoverFile(null);
    setExistingCoverUrl("");
    setExistingImages([]);

    setGalleryFiles((prev) => {
      (prev || []).forEach((f) => revokePreviewUrl(f));
      return [];
    });

    setSelectedSlug("");
    setMsg("");
    resetProgress();
  }, [mode]);

  const openResult = ({ tone = "ok", title, subtitle, lines = [], okText = "OK" }) => {
    setResult({ open: true, tone, title, subtitle, lines, okText });
  };

  const closeResult = () => {
    setResult((r) => ({ ...r, open: false }));
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {}
  };

  const previewsRef = useRef(new Map());

  const getPreviewUrl = (file) => {
    if (!file) return "";
    const m = previewsRef.current;
    if (m.has(file)) return m.get(file);
    const url = URL.createObjectURL(file);
    m.set(file, url);
    return url;
  };

  const handleCreateSection = async () => {
    const label = newSectionLabel.trim();
    const slug = slugify(label);

    if (!label) {
      setMsg("Nome sezione obbligatorio.");
      return;
    }

    if (!slug) {
      setMsg("Slug non valido.");
      return;
    }

    setBusy(true);
    setMsg("");

    try {
      await createSection({
        slug,
        label,
        order: Number(newSectionOrder) || 999,
        visible: true,
      });

      const updated = await listSections();
      setSections(updated);
      setSection(slug);

      setNewSectionLabel("");
      setNewSectionOrder(999);
      setSectionModalOpen(false);

      openResult({
        tone: "ok",
        title: "Sezione creata ✅",
        subtitle: label,
        lines: [`Slug: ${slug}`],
        okText: "OK",
      });
    } catch (e) {
      setMsg(String(e?.message || e));
      openResult({
        tone: "err",
        title: "Creazione sezione fallita ❌",
        subtitle: "La sezione non è stata creata",
        lines: [String(e?.message || e)],
        okText: "OK",
      });
    } finally {
      setBusy(false);
    }
  };

  const revokePreviewUrl = (file) => {
    const m = previewsRef.current;
    const url = m.get(file);
    if (url) URL.revokeObjectURL(url);
    m.delete(file);
  };

  useEffect(() => {
    let alive = true;

    (async () => {
      if (!user || !isAdmin) return;

      try {
        const data = await listSections();
        if (!alive) return;

        setSections(data);

        if (data.length > 0 && !data.some((s) => s.slug === section)) {
          setSection(data[0].slug);
        }
      } catch (e) {
        console.error("Errore caricando sezioni:", e);
      }
    })();

    return () => {
      alive = false;
    };
  }, [user, isAdmin, section]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u || null);
      setIsAdmin(false);
      if (!u) return;
      const a = await getDoc(doc(db, "admins", u.uid));
      setIsAdmin(a.exists());
    });
    return () => unsub();
  }, []);

  const resetProgress = () => {
    setPhase("");
    setProgress(0);
    setProgressLabel("");
    setEtaMs(null);
    startMsRef.current = 0;
    durationsRef.current = [];
    totalItemsRef.current = 0;
  };

  const updateEta = (doneItems) => {
    const total = totalItemsRef.current || 0;
    const remaining = Math.max(0, total - doneItems);
    const durs = durationsRef.current;
    const avg = durs.length ? durs.reduce((a, b) => a + b, 0) / durs.length : 0;
    if (!avg || !Number.isFinite(avg)) {
      setEtaMs(null);
      return;
    }
    setEtaMs(avg * remaining);
  };

  useEffect(() => {
    const m = previewsRef.current;
    return () => {
      for (const url of m.values()) URL.revokeObjectURL(url);
      m.clear();
    };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!user || !isAdmin) return;
      if (mode !== "edit") return;

      setMsg("");
      setPhase("load");
      setProgressLabel("Carico elenco…");
      setProgress(10);

      try {
        const list = await listEntriesBySection(section);
        if (!alive) return;
        setEntriesList(list);
        setSelectedSlug(list?.[0]?.slug || "");
        setProgress(100);
        setProgressLabel("Elenco caricato ✅");
        setTimeout(() => resetProgress(), 400);
      } catch (e) {
        if (!alive) return;
        setEntriesList([]);
        setSelectedSlug("");
        setMsg(String(e?.message || e));
        setPhase("error");
        setProgressLabel("Errore caricando elenco.");
      }
    })();

    return () => {
      alive = false;
    };
  }, [mode, section, user, isAdmin]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (mode !== "edit") return;

      setShownCount(PAGE_SIZE);

      if (!selectedSlug) {
        setExistingCoverUrl("");
        setExistingImages([]);
        setTitolo("");
        setDescrizione("");
        return;
      }

      setMsg("");
      resetProgress();
      setPhase("load");
      setProgress(12);
      setProgressLabel("Carico card…");

      try {
        const entry = await getEntryBySectionSlug(section, selectedSlug);
        if (!alive) return;
        if (!entry) {
          setExistingCoverUrl("");
          setExistingImages([]);
          setTitolo("");
          setDescrizione("");
          setMsg("Entry non trovata.");
          return;
        }

        setTitolo(entry.titolo || "");
        setDescrizione(entry.descrizione || "");
        setExistingCoverUrl(entry.coverUrl || "");
        setExistingImages(Array.isArray(entry.images) ? entry.images : []);

        setCoverFile(null);
        setGalleryFiles((prev) => {
          (prev || []).forEach((f) => revokePreviewUrl(f));
          return [];
        });

        setProgress(100);
        setProgressLabel("Card caricata ✅");
        setTimeout(() => resetProgress(), 450);
      } catch (e) {
        if (!alive) return;
        setMsg(String(e?.message || e));
        setPhase("error");
        setProgressLabel("Errore caricando la card.");
      }
    })();

    return () => {
      alive = false;
    };
  }, [mode, section, selectedSlug]);

  const effectiveSlug = mode === "create" ? slugAuto : selectedSlug;

  const addGalleryFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;

    setGalleryFiles((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      const key = (f) => `${f.name}__${f.size}__${f.lastModified}`;
      const map = new Map(prevArr.map((f) => [key(f), f]));
      for (const f of incoming) map.set(key(f), f);
      return Array.from(map.values());
    });
  };

  const removeSelectedGalleryFile = (file) => {
    setGalleryFiles((prev) => {
      const next = (prev || []).filter((f) => f !== file);
      revokePreviewUrl(file);
      return next;
    });
  };

  const clearSelectedGallery = () => {
    setGalleryFiles((prev) => {
      (prev || []).forEach((f) => revokePreviewUrl(f));
      return [];
    });
  };

  const createNew = async () => {
    setMsg("");
    resetProgress();

    if (!user) return setMsg("Devi fare login.");
    if (!isAdmin) return setMsg("Utente non admin (manca admins/{uid}).");
    if (!titolo.trim()) return setMsg("Titolo obbligatorio.");
    if (!slugAuto) return setMsg("Slug non valido.");
    if (!coverFile) return setMsg("Cover obbligatoria.");

    const slug = slugAuto;
    const titleForResult = titolo?.trim() || slug;

    setBusy(true);
    totalItemsRef.current = 1 + (galleryFiles?.length || 0);
    startMsRef.current = Date.now();

    try {
      setPhase("cover");
      setProgress(6);
      setProgressLabel("Carico la cover…");
      updateEta(0);

      const t0 = Date.now();
      const coverUrl = await uploadFile({ section, slug, file: coverFile, kind: "cover" });
      const t1 = Date.now();
      durationsRef.current.push(t1 - t0);

      setProgress(18);
      setProgressLabel("Cover caricata ✅");
      updateEta(1);

      setPhase("gallery");
      const total = galleryFiles.length;
      const images = [];

      if (total > 0) {
        for (let idx = 0; idx < total; idx++) {
          const f = galleryFiles[idx];
          setProgressLabel(`Carico galleria ${idx + 1}/${total}…`);

          const g0 = Date.now();
          const url = await uploadFile({ section, slug, file: f, kind: "gallery" });
          const g1 = Date.now();
          durationsRef.current.push(g1 - g0);

          images.push(url);

          const start = 18;
          const end = 86;
          const step = (idx + 1) / total;
          setProgress(clamp(Math.round(start + (end - start) * step), 0, 100));
          updateEta(1 + (idx + 1));
        }
      } else {
        setProgress(86);
        setProgressLabel("Nessuna immagine galleria selezionata.");
        updateEta(1);
      }

      setPhase("saving");
      setProgressLabel("Salvo su Firestore…");
      setProgress(92);

      await upsertEntry({ section, slug, titolo, descrizione, coverUrl, images });

      setProgress(100);
      setProgressLabel("Completato ✅");
      setEtaMs(0);

      setTitolo("");
      setDescrizione("");
      setCoverFile(null);
      clearSelectedGallery();

      setTimeout(() => {
        openResult({
          tone: "ok",
          title: "Card creata ✅",
          subtitle: titleForResult,
          lines: [
            `Sezione: ${section}`,
            `Slug: ${slug}`,
            images.length
              ? `Galleria caricata: ${images.length} foto`
              : "Galleria: 0 foto (scelta minimal, rispetto).",
          ],
          okText: "OK, torna al pannello",
        });
      }, 0);
    } catch (e) {
      setMsg(String(e?.message || e));
      setPhase("error");
      setProgressLabel("Errore durante il caricamento.");
      setEtaMs(null);

      setTimeout(() => {
        openResult({
          tone: "err",
          title: "Operazione fallita ❌",
          subtitle: "Creazione card non completata",
          lines: [String(e?.message || e)],
          okText: "OK",
        });
      }, 0);
    } finally {
      setBusy(false);
      setTimeout(() => resetProgress(), 250);
    }
  };

  const saveEdits = async () => {
    setMsg("");
    resetProgress();

    if (!user) return setMsg("Devi fare login.");
    if (!isAdmin) return setMsg("Utente non admin (manca admins/{uid}).");
    if (!selectedSlug) return setMsg("Seleziona una card da modificare.");

    const slug = selectedSlug;
    const titleForResult = titolo?.trim() || selectedSlug;

    const coverWillUpload = !!coverFile;
    const newGalleryCount = galleryFiles?.length || 0;

    totalItemsRef.current = (coverWillUpload ? 1 : 0) + newGalleryCount;
    startMsRef.current = Date.now();

    setBusy(true);

    try {
      let coverUrl = existingCoverUrl || "";
      let done = 0;

      if (coverWillUpload) {
        setPhase("cover");
        setProgress(8);
        setProgressLabel("Carico nuova cover…");
        updateEta(done);

        const t0 = Date.now();
        const newCoverUrl = await uploadFile({ section, slug, file: coverFile, kind: "cover" });
        const t1 = Date.now();
        durationsRef.current.push(t1 - t0);
        done += 1;

        if (existingCoverUrl) {
          try {
            await deleteFileByUrl(existingCoverUrl);
          } catch {}
        }

        coverUrl = newCoverUrl;
        setProgress(28);
        setProgressLabel("Cover aggiornata ✅");
        updateEta(done);
      }

      setPhase("gallery");
      let appended = [];
      const total = newGalleryCount;

      if (total > 0) {
        for (let idx = 0; idx < total; idx++) {
          const f = galleryFiles[idx];
          setProgressLabel(`Aggiungo foto ${idx + 1}/${total}…`);

          const g0 = Date.now();
          const url = await uploadFile({ section, slug, file: f, kind: "gallery" });
          const g1 = Date.now();
          durationsRef.current.push(g1 - g0);

          appended.push(url);
          done += 1;

          const start = coverWillUpload ? 28 : 10;
          const end = 86;
          const step = (idx + 1) / total;
          setProgress(clamp(Math.round(start + (end - start) * step), 0, 100));
          updateEta(done);
        }
      } else {
        setProgress(86);
        setProgressLabel("Nessuna nuova immagine da aggiungere.");
        updateEta(done);
      }

      setPhase("saving");
      setProgressLabel("Salvo modifiche su Firestore…");
      setProgress(92);

      const nextImages = [...(existingImages || []), ...appended];

      await patchEntry(section, slug, {
        titolo: titolo || "",
        descrizione: descrizione || "",
        coverUrl,
        images: nextImages,
      });

      setProgress(100);
      setProgressLabel("Modifiche salvate ✅");
      setEtaMs(0);

      setExistingCoverUrl(coverUrl);
      setExistingImages(nextImages);
      setCoverFile(null);
      clearSelectedGallery();

      setTimeout(() => {
        openResult({
          tone: "ok",
          title: "Modifiche salvate ✅",
          subtitle: titleForResult,
          lines: [
            `Sezione: ${section}`,
            `Slug: ${slug}`,
            coverWillUpload ? "Cover: aggiornata" : "Cover: invariata",
            newGalleryCount ? `Foto aggiunte: ${newGalleryCount}` : "Foto aggiunte: 0",
          ],
          okText: "OK, torna al pannello",
        });
      }, 0);
    } catch (e) {
      setMsg(String(e?.message || e));
      setPhase("error");
      setProgressLabel("Errore durante il salvataggio.");
      setEtaMs(null);

      setTimeout(() => {
        openResult({
          tone: "err",
          title: "Salvataggio fallito ❌",
          subtitle: titleForResult,
          lines: [String(e?.message || e)],
          okText: "OK",
        });
      }, 0);
    } finally {
      setBusy(false);
      setTimeout(() => resetProgress(), 250);
    }
  };

  const removeExistingImage = async (url) => {
    if (!selectedSlug) return;

    setMsg("");
    resetProgress();
    setBusy(true);

    try {
      setPhase("delete");
      setProgress(30);
      setProgressLabel("Rimuovo foto…");

      await deleteFileByUrl(url);

      const next = (existingImages || []).filter((u) => u !== url);
      await patchEntry(section, selectedSlug, { images: next });

      setExistingImages(next);

      setProgress(100);
      setProgressLabel("Foto rimossa ✅");
      setEtaMs(0);
    } catch (e) {
      setMsg(String(e?.message || e));
      setPhase("error");
      setProgressLabel("Errore rimuovendo la foto.");

      setTimeout(() => {
        openResult({
          tone: "err",
          title: "Rimozione fallita ❌",
          subtitle: titolo?.trim() || selectedSlug,
          lines: [String(e?.message || e)],
          okText: "OK",
        });
      }, 0);
    } finally {
      setBusy(false);
      setTimeout(() => resetProgress(), 250);
    }
  };

  const deleteWholeCard = async () => {
    if (!selectedSlug) return;

    const titleForResult = titolo?.trim() || selectedSlug;
    const slug = selectedSlug;

    setMsg("");
    resetProgress();
    setBusy(true);

    try {
      setPhase("delete");
      setProgress(12);
      setProgressLabel("Elimino card…");

      if (deleteAlsoFiles) {
        const urls = [];
        if (existingCoverUrl) urls.push(existingCoverUrl);
        for (const u of existingImages || []) urls.push(u);

        totalItemsRef.current = urls.length || 1;
        startMsRef.current = Date.now();

        let done = 0;
        for (const u of urls) {
          try {
            await deleteFileByUrl(u);
          } catch {}
          done += 1;
          const start = 12;
          const end = 78;
          const step = urls.length ? done / urls.length : 1;
          setProgress(clamp(Math.round(start + (end - start) * step), 0, 100));
          setProgressLabel(`Elimino media ${done}/${urls.length}…`);
          updateEta(done);
        }
      } else {
        setProgress(78);
        setProgressLabel("Media lasciati su Storage.");
      }

      setPhase("saving");
      setProgress(88);
      setProgressLabel("Rimuovo entry da Firestore…");

      await deleteEntry(section, slug);

      setProgress(100);
      setProgressLabel("Card rimossa ✅");
      setEtaMs(0);

      let list = [];
      try {
        list = await listEntriesBySection(section);
        setEntriesList(list);
        setSelectedSlug(list?.[0]?.slug || "");
      } catch {
        setEntriesList([]);
        setSelectedSlug("");
      }

      setExistingCoverUrl("");
      setExistingImages([]);
      setCoverFile(null);
      clearSelectedGallery();

      setConfirmDeleteOpen(false);

      setTimeout(() => {
        openResult({
          tone: "warn",
          title: "Card eliminata 🗑️✅",
          subtitle: titleForResult,
          lines: [
            `Sezione: ${section}`,
            `Slug: ${slug}`,
            deleteAlsoFiles ? "Media: eliminati anche da Storage" : "Media: lasciati su Storage",
            list?.length ? `Card rimaste in sezione: ${list.length}` : "Sezione: vuota",
          ],
          okText: "OK, torna al pannello",
        });
      }, 0);
    } catch (e) {
      setMsg(String(e?.message || e));
      setPhase("error");
      setProgressLabel("Errore eliminando la card.");

      setTimeout(() => {
        openResult({
          tone: "err",
          title: "Eliminazione fallita ❌",
          subtitle: titleForResult,
          lines: [String(e?.message || e)],
          okText: "OK",
        });
      }, 0);
    } finally {
      setBusy(false);
      setTimeout(() => resetProgress(), 250);
    }
  };

  if (!user) {
    return (
      <main className="min-h-screen grid place-items-center bg-[radial-gradient(ellipse_at_top,rgba(204,58,33,.14),transparent_55%),linear-gradient(180deg,#f6f2ee_0%,#fcfbfa_40%,#f8fafc_100%)] p-6">
        <div className="w-full max-w-md rounded-3xl border border-slate-200/80 bg-white/85 p-6 backdrop-blur-2xl shadow-2xl">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-2xl bg-slate-900 text-white">
              <Shield className="h-5 w-5" />
            </div>
            <div>
              <div className="text-lg font-black text-slate-900">Login richiesto</div>
              <div className="text-sm font-semibold text-slate-600">Vai su /login</div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="min-h-screen grid place-items-center bg-[radial-gradient(ellipse_at_top,rgba(204,58,33,.14),transparent_55%),linear-gradient(180deg,#f6f2ee_0%,#fcfbfa_40%,#f8fafc_100%)] p-6">
        <div className="w-full max-w-xl rounded-3xl border border-slate-200/80 bg-white/85 p-6 backdrop-blur-2xl shadow-2xl">
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-2xl bg-amber-500 text-white shadow-lg">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="text-lg font-black text-slate-900">Non sei admin</div>
              <div className="mt-1 text-sm font-semibold text-slate-700">
                Crea in Firestore: <span className="font-black">admins/{user.uid}</span>
              </div>
              <button
                onClick={() => signOut(auth)}
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-black text-white shadow-lg transition hover:bg-black"
              >
                <LogOut className="h-4 w-4" />
                Esci
              </button>
            </div>
          </div>
        </div>
      </main>
    );
  }

  const shownImages = existingImages.slice(0, shownCount);
  const canShowMore = existingImages.length > shownCount;

  return (
<main className="mx-auto w-[min(1280px,94%)] pt-28 pb-10 sm:pt-32 sm:pb-12">      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-[#CC3A21]/12 blur-3xl" />
        <div className="absolute -right-24 top-24 h-80 w-80 rounded-full bg-amber-200/18 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-96 w-96 rounded-full bg-[#E58E7C]/10 blur-3xl" />
      </div>

      <div className="relative mx-auto w-[min(1100px,96%)]">
        <div className="relative overflow-hidden rounded-3xl border border-slate-200/80 bg-white/12 shadow-[0_25px_100px_rgba(2,6,23,.10)] backdrop-blur-2xl">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,.22),transparent_60%)]" />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#CC3A21,#E58E7C,#F2C9A5)]" />

          <div className="relative p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-3">
                <div className="grid h-12 w-12 place-items-center rounded-3xl bg-white/92 text-slate-900 shadow-xl">
                  <LayoutDashboard className="h-6 w-6" />
                </div>
                <div>
                  <h1 className="text-3xl font-black tracking-tight text-slate-900">
                    Admin MuDiF{" "}
                    <span className="inline-flex items-center gap-1 align-middle text-sm font-black text-[#7A2417]">
                      <Sparkles className="h-4 w-4" /> Studio
                    </span>
                  </h1>
                  <div className="mt-1 text-sm font-semibold text-slate-600">
                    {user.email}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Badge tone="dark">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  admin attivo
                </Badge>
                <button
                  onClick={() => signOut(auth)}
                  className="inline-flex items-center gap-2 rounded-2xl bg-white/92 px-4 py-3 text-sm font-black text-slate-900 shadow-xl transition hover:bg-white"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              <button
  disabled={busy}
  onClick={() => setMode("create")}
  className={`inline-flex min-h-[58px] w-full items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-center text-sm font-black leading-tight transition ${
    mode === "create"
      ? "border-slate-900 bg-slate-900 text-white shadow-[0_18px_40px_rgba(15,23,42,.18)]"
      : "border-slate-300 bg-white text-slate-900 shadow-sm hover:-translate-y-[1px] hover:border-slate-400 hover:bg-slate-50"
  } ${busy ? "opacity-60" : ""}`}
>
  <PlusCircle className="h-4 w-4 shrink-0" />
  <span className="break-words">Crea card in sezione esistente</span>
</button>

<button
  disabled={busy}
  onClick={() => setMode("edit")}
  className={`inline-flex min-h-[58px] w-full items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-center text-sm font-black leading-tight transition ${
    mode === "edit"
      ? "border-slate-900 bg-slate-900 text-white shadow-[0_18px_40px_rgba(15,23,42,.18)]"
      : "border-slate-300 bg-white text-slate-900 shadow-sm hover:-translate-y-[1px] hover:border-slate-400 hover:bg-slate-50"
  } ${busy ? "opacity-60" : ""}`}
>
  <Pencil className="h-4 w-4 shrink-0" />
  <span className="break-words">Modifica card</span>
</button>

<button
  disabled={busy}
  onClick={() => {
    const nextOrder =
      sections.length > 0
        ? Math.max(...sections.map((s) => Number(s.order) || 0)) + 1
        : 1;

    setNewSectionOrder(nextOrder);
    setSectionModalOpen(true);
  }}
  className={`inline-flex min-h-[58px] w-full items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-center text-sm font-black leading-tight transition ${
    busy
      ? "opacity-60"
      : "border-[#CC3A21]/25 bg-[#CC3A21]/[0.06] text-[#7A2417] shadow-sm hover:-translate-y-[1px] hover:border-[#CC3A21]/40 hover:bg-[#CC3A21]/[0.10]"
  }`}
>
  <Folder className="h-4 w-4 shrink-0" />
  <span className="break-words">Crea nuova sezione</span>
</button>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <Card
            title={mode === "create" ? "Crea nuova card" : "Modifica card esistente"}
            icon={mode === "create" ? <PlusCircle className="h-5 w-5" /> : <Pencil className="h-5 w-5" />}
            right={
              <div className="flex items-center gap-2">
                <div className="hidden items-center gap-2 md:flex">
                  <Badge>
                    <Folder className="h-3.5 w-3.5" />
                    {section}
                  </Badge>
                  <Badge>
                    <Hash className="h-3.5 w-3.5" />
                    {effectiveSlug || "—"}
                  </Badge>
                </div>

                {mode === "edit" && (
                  <button
                    disabled={busy || !selectedSlug}
                    onClick={() => setConfirmDeleteOpen(true)}
                    className="inline-flex items-center gap-2 rounded-2xl bg-rose-600 px-4 py-2 text-xs font-black text-white shadow-lg transition hover:bg-rose-700 disabled:opacity-60"
                    title="Rimuovi card intera"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Elimina card</span>
                    <span className="sm:hidden">Elimina</span>
                  </button>
                )}
              </div>
            }
          >
            {mode === "edit" && (
              <div className="mb-5 rounded-3xl border border-slate-200/80 bg-white/70 p-4 shadow-[0_18px_70px_rgba(2,6,23,.08)] backdrop-blur-2xl md:hidden">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs font-black text-slate-700">Azioni rapide</div>
                    <div className="mt-1 truncate text-sm font-semibold text-slate-700">
                      {section} / <span className="font-black">{selectedSlug || "—"}</span>
                    </div>
                  </div>
                  <button
                    disabled={busy || !selectedSlug}
                    onClick={() => setConfirmDeleteOpen(true)}
                    className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-rose-600 px-4 py-3 text-sm font-black text-white shadow-lg transition hover:bg-rose-700 disabled:opacity-60"
                  >
                    <Trash2 className="h-5 w-5" />
                    Elimina
                  </button>
                </div>
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-xs font-black text-slate-700">Sezione</label>
                <select
                  value={section}
                  disabled={busy}
                  onChange={(e) => setSection(e.target.value)}
                  className="mt-1 w-full rounded-2xl border border-slate-900/10 bg-white/90 px-4 py-3 text-sm font-semibold outline-none focus:ring-4 focus:ring-[#CC3A21]/20 disabled:opacity-60"
                >
                  {sections.map((s) => (
                    <option key={s.slug} value={s.slug}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>

              {mode === "edit" ? (
                <div>
                  <label className="text-xs font-black text-slate-700">Seleziona card</label>
                  <select
                    value={selectedSlug}
                    disabled={busy}
                    onChange={(e) => setSelectedSlug(e.target.value)}
                    className="mt-1 w-full rounded-2xl border border-slate-900/10 bg-white/90 px-4 py-3 text-sm font-semibold outline-none focus:ring-4 focus:ring-[#CC3A21]/20 disabled:opacity-60"
                  >
                    {entriesList.map((e) => (
                      <option key={e.slug} value={e.slug}>
                        {e.titolo ? `${e.titolo}` : e.slug}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="text-xs font-black text-slate-700">Slug (auto)</label>
                  <div className="mt-1 w-full rounded-2xl border border-slate-900/10 bg-slate-900/[0.04] px-4 py-3 text-sm font-black text-slate-700">
                    {slugAuto || "—"}
                  </div>
                </div>
              )}

              <div className="md:col-span-2">
                <label className="text-xs font-black text-slate-700">Titolo</label>
                <input
                  value={titolo}
                  disabled={busy}
                  onChange={(e) => setTitolo(e.target.value)}
                  className="mt-1 w-full rounded-2xl border border-slate-900/10 bg-white/90 px-4 py-3 text-sm font-semibold outline-none focus:ring-4 focus:ring-[#CC3A21]/20 disabled:opacity-60"
                  placeholder="Es. Fondo Rossi, Pagani 1962…"
                />
                {mode === "edit" && (
                  <div className="mt-2 text-xs font-semibold text-slate-600">
                    Slug in modifica: <span className="font-black">{effectiveSlug || "—"}</span>
                  </div>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="text-xs font-black text-slate-700">Descrizione</label>
                <textarea
                  value={descrizione}
                  disabled={busy}
                  onChange={(e) => setDescrizione(e.target.value)}
                  className="mt-1 min-h-[120px] w-full rounded-2xl border border-slate-900/10 bg-white/90 px-4 py-3 text-sm font-semibold outline-none focus:ring-4 focus:ring-[#CC3A21]/20 disabled:opacity-60"
                  placeholder="Testo breve…"
                />
              </div>

              <div className="rounded-3xl border border-slate-900/10 bg-white/65 p-5 backdrop-blur-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ImageIcon className="h-4 w-4 text-slate-700" />
                    <div className="text-sm font-black text-slate-900">Cover</div>
                  </div>
                  <Badge>
                    <Upload className="h-3.5 w-3.5" />
                    {mode === "create" ? "obbligatoria" : "opzionale"}
                  </Badge>
                </div>

                <input
                  type="file"
                  accept="image/*"
                  disabled={busy}
                  onChange={(e) => setCoverFile(e.target.files?.[0] || null)}
                  className="mt-3 w-full rounded-2xl border border-slate-900/10 bg-white/90 px-4 py-3 text-sm font-semibold disabled:opacity-60"
                />

                {coverFile && (
                  <div className="mt-3 rounded-2xl border border-slate-900/10 bg-white p-3">
                    <div className="text-xs font-black text-slate-800">Nuova cover</div>
                    <div className="mt-1 truncate text-[12px] font-semibold text-slate-600">
                      {coverFile.name} • {fmtBytes(coverFile.size)}
                    </div>
                  </div>
                )}

                {mode === "edit" && existingCoverUrl && (
                  <div className="mt-4 overflow-hidden rounded-2xl border border-slate-900/10 bg-white">
                    <img src={existingCoverUrl} alt="cover" className="h-44 w-full object-cover" loading="lazy" />
                    <div className="p-3 text-xs font-semibold text-slate-600">Cover attuale</div>
                  </div>
                )}
              </div>

              <div className="rounded-3xl border border-slate-900/10 bg-white/65 p-5 backdrop-blur-xl">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Images className="h-4 w-4 text-slate-700" />
                    <div className="text-sm font-black text-slate-900">Gallery</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge>
                      <FileText className="h-3.5 w-3.5" />
                      {mode === "create" ? "multipla" : "aggiunta"}
                    </Badge>

                    {galleryFiles.length > 0 && (
                      <button
                        disabled={busy}
                        onClick={clearSelectedGallery}
                        className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-3 py-2 text-xs font-black text-white shadow-lg transition hover:bg-black disabled:opacity-60"
                        title="Svuota selezione"
                      >
                        <X className="h-4 w-4" />
                        Svuota
                      </button>
                    )}
                  </div>
                </div>

                <input
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={busy}
                  onChange={(e) => addGalleryFiles(e.target.files)}
                  className="mt-3 w-full rounded-2xl border border-slate-900/10 bg-white/90 px-4 py-3 text-sm font-semibold disabled:opacity-60"
                />

                {galleryFiles.length > 0 && (
                  <>
                    <div className="mt-2 text-xs font-semibold text-slate-600">
                      Selezionate: <span className="font-black">{galleryFiles.length}</span>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                      {galleryFiles.map((f) => (
                        <div
                          key={`${f.name}-${f.size}-${f.lastModified}`}
                          className="group overflow-hidden rounded-3xl border border-slate-900/10 bg-white shadow-[0_18px_70px_rgba(2,6,23,.06)]"
                        >
                          <div className="relative">
                            <img src={getPreviewUrl(f)} alt={f.name} className="h-28 w-full object-cover" loading="lazy" />
                            <button
                              disabled={busy}
                              onClick={() => removeSelectedGalleryFile(f)}
                              className="absolute right-2 top-2 inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-white/90 text-slate-900 shadow-lg transition hover:bg-rose-50 hover:text-rose-700 disabled:opacity-60"
                              title="Rimuovi dalla selezione"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="p-3">
                            <div className="truncate text-[11px] font-black text-slate-900">{f.name}</div>
                            <div className="mt-0.5 text-[11px] font-semibold text-slate-600">{fmtBytes(f.size)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {mode === "edit" && (
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs font-black text-slate-700">Galleria attuale</div>
                    <Badge>
                      <Camera className="h-3.5 w-3.5" />
                      {existingImages.length} foto
                    </Badge>
                  </div>

                  {existingImages.length === 0 ? (
                    <div className="mt-3 rounded-3xl border border-slate-900/10 bg-white/70 p-5 text-sm font-semibold text-slate-600">
                      Nessuna foto in galleria.
                    </div>
                  ) : (
                    <>
                      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                        {shownImages.map((url) => (
                          <div
                            key={url}
                            className="group overflow-hidden rounded-3xl border border-slate-900/10 bg-white shadow-[0_18px_70px_rgba(2,6,23,.08)]"
                          >
                            <div className="relative">
                              <img src={url} alt="gallery" className="h-32 w-full object-cover" loading="lazy" />
                              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/25 to-transparent opacity-0 transition group-hover:opacity-100" />
                            </div>

                            <button
                              disabled={busy}
                              onClick={() => removeExistingImage(url)}
                              className="inline-flex w-full items-center justify-center gap-2 border-t border-slate-900/10 bg-white px-3 py-2 text-xs font-black text-slate-900 transition hover:bg-rose-50 hover:text-rose-900 disabled:opacity-60"
                            >
                              <Trash2 className="h-4 w-4" />
                              Rimuovi
                            </button>
                          </div>
                        ))}
                      </div>

                      {canShowMore && (
                        <div className="mt-5 flex justify-center">
                          <button
                            disabled={busy}
                            onClick={() => setShownCount((c) => Math.min(existingImages.length, c + PAGE_SIZE))}
                            className="rounded-2xl bg-slate-900 px-5 py-3 text-sm font-black text-white shadow-xl transition hover:bg-black disabled:opacity-60"
                          >
                            Mostra altre ({Math.min(PAGE_SIZE, existingImages.length - shownCount)})
                          </button>
                        </div>
                      )}

                      {existingImages.length > PAGE_SIZE && shownCount > PAGE_SIZE && (
                        <div className="mt-3 flex justify-center">
                          <button
                            disabled={busy}
                            onClick={() => setShownCount(PAGE_SIZE)}
                            className="text-xs font-black text-slate-700 hover:text-slate-900"
                          >
                            Mostra solo le prime 20
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {mode === "create" ? (
              <button
                disabled={busy}
                onClick={createNew}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-4 text-sm font-black text-white shadow-[0_18px_70px_rgba(2,6,23,.20)] transition hover:bg-black disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <PlusCircle className="h-5 w-5" />}
                {busy ? "Operazione in corso..." : "Crea card"}
              </button>
            ) : (
              <button
                disabled={busy}
                onClick={saveEdits}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-4 text-sm font-black text-white shadow-[0_18px_70px_rgba(2,6,23,.20)] transition hover:bg-black disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Pencil className="h-5 w-5" />}
                {busy ? "Operazione in corso..." : "Salva modifiche"}
              </button>
            )}

            {msg && (
              <div className="mt-5 rounded-2xl border border-slate-200/80 bg-white/75 p-4 text-sm font-semibold text-slate-800 backdrop-blur-xl">
                {msg}
              </div>
            )}
          </Card>
        </div>
      </div>

      <ProgressModal
        open={busy}
        phase={phase}
        progress={progress}
        label={progressLabel}
        etaMs={etaMs}
      />

      <ConfirmDeleteModal
        open={confirmDeleteOpen}
        busy={busy}
        section={section}
        selectedSlug={selectedSlug}
        deleteAlsoFiles={deleteAlsoFiles}
        setDeleteAlsoFiles={setDeleteAlsoFiles}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={deleteWholeCard}
      />

      <ResultModal
        open={result.open}
        tone={result.tone}
        title={result.title}
        subtitle={result.subtitle}
        lines={result.lines}
        okText={result.okText}
        onOk={closeResult}
      />

      <CreateSectionModal
        open={sectionModalOpen}
        busy={busy}
        label={newSectionLabel}
        setLabel={setNewSectionLabel}
        order={newSectionOrder}
        setOrder={setNewSectionOrder}
        onClose={() => setSectionModalOpen(false)}
        onConfirm={handleCreateSection}
      />
    </main>
  );
}